import React, { useCallback, useMemo } from 'react'

import { Modal, T, VStack, HStack } from 'govwell-ui'
import { AICommunityAssistantUsageTier } from 'types/graphql'

import AIAssistantUpsellPricingMatrix from 'src/components/AIAssistant/AIAssistantUpsellPricingMatrix'
import AIAssistantUpsellTier from 'src/components/AIAssistant/AIAssistantUpsellTier'
import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import { useNotifyGovWellSalesTeamAboutHigherAIAssistantTier } from 'src/fetch/notify-sales-team'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
type Props = {
  onClose: () => void | Promise<void>
  tiers: AICommunityAssistantUsageTier[]
}

const AIAssistantUpsellModal = ({ onClose, tiers }: Props) => {
  const organizationId = useMyOrgId()
  const { notification } = useNotification()
  const { mutateAsync: notifyGovWellSalesTeamAboutHigherAIAssistantTier } =
    useNotifyGovWellSalesTeamAboutHigherAIAssistantTier()

  const handleNotifySalesTeam = useCallback(
    async (tier: AICommunityAssistantUsageTier) => {
      await notifyGovWellSalesTeamAboutHigherAIAssistantTier({ tier: tier.id, organizationId })
      notification.success({
        message: `Thank you for your interest! Our Sales team has been notified and will be in touch soon.`,
      })
      await onClose()
    },
    [notifyGovWellSalesTeamAboutHigherAIAssistantTier, onClose, notification, organizationId]
  )

  // Legacy orgs (AicaLegacyTrial) keep the original card-grid layout. The
  // layout choice is implicit in the tier set: getTiersForOrg on the backend
  // only ever returns AicaLegacyTrial inside the legacy card list, and never
  // alongside the new-system tiers. We infer rather than expose an explicit
  // `layout` field on the GraphQL response because the legacy layout is
  // expected to be removed once all legacy orgs are migrated (see the 2026-04
  // reverse-trial spec, Phase 3) — adding a transient enum field would just
  // be removed soon after. If the legacy layout sticks around longer than
  // anticipated, promote this to an explicit backend field.
  const isLegacyLayout = useMemo(() => tiers.some((tier) => tier.id === 'AicaLegacyTrial'), [tiers])

  if (isLegacyLayout) {
    return (
      <Modal onClose={onClose} width="75%">
        <Modal.Header>Pricing</Modal.Header>
        <Modal.Content>
          <VStack align="start" gap={4} className="w-full">
            <T.BodyLarge className="text-neutral-600">
              Start for free. Upgrade as you go.
            </T.BodyLarge>

            <HStack wrap="wrap" justify="space-between" align="stretch" gap={4}>
              {tiers.map((tier) => (
                <AIAssistantUpsellTier
                  key={tier.id}
                  tier={tier}
                  isDisabled={tier.isDisabled ?? false}
                  handleNotifySalesTeam={handleNotifySalesTeam}
                />
              ))}
            </HStack>
          </VStack>
        </Modal.Content>
      </Modal>
    )
  }

  return (
    <Modal onClose={onClose} width="min(1200px, 92vw)" height="calc(100vh - 160px)">
      <Modal.Content>
        <AIAssistantUpsellPricingMatrix
          tiers={tiers}
          handleNotifySalesTeam={handleNotifySalesTeam}
        />
      </Modal.Content>
    </Modal>
  )
}

export default React.memo(AIAssistantUpsellModal)
